import { BehaviorSubject, Observable, Subscription } from 'rxjs';

/**
 * @inspiredby @angular/ck/collections/data-source.ts
 */
export abstract class DataSource<T> {
  abstract connect(collectionViewer: any): Observable<T[] | ReadonlyArray<T>>;

  abstract disconnect(collectionViewer: any): void;
}

/**
 * Data source that accepts a client-side data array.
 * @inspiredby @angular/material/table/table-data-source.ts
 */
export class TableListDataSource<T> extends DataSource<T> {
  /** Stream that emits when a new data array is set on the data source. */
  private readonly _data: BehaviorSubject<T[]>;

  /** Stream emitting render data to the table (depends on ordered data changes). */
  private readonly _renderData = new BehaviorSubject<T[]>([]);

  /**
   * Subscription to the changes that should trigger an update to the table's rendered rows, such
   * as filtering, sorting, pagination, or base data changes.
   */
  _renderChangesSubscription = Subscription.EMPTY;

  constructor(initialData: T[] = []) {
    super();
    this._data = new BehaviorSubject<T[]>(initialData);
    this._updateChangesSubscription();
  }

  /**
   * Subscribe to changes that should trigger an update to the table's rendered rows
   */
  _updateChangesSubscription() {
    const dataStream = this._data;

    // Watched for data changes and send the result to the table to render.
    this._renderChangesSubscription.unsubscribe();
    this._renderChangesSubscription = dataStream.subscribe((data) => this._renderData.next(data));
  }

  /**
   * Get and set input data
   */
  data(data: T[]) {
    if (data) {
      return this._data.next(data);
    } else {
      return this._data.value;
    }
  }

  /**
   * Called when it connects to the data source.
   */
  connect() {
    return this._renderData;
  }

  /**
   * Called when it is destroyed. No-op
   */
  disconnect() {}
}

export type TableListDataSourceInput<T> = DataSource<T> | Observable<ReadonlyArray<T> | T[]> | ReadonlyArray<T> | T[];

/** Checks whether an object is a data source. */
export declare function isDataSource(value: any): value is DataSource<any>;
